import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { Button, TextInput } from '@kcftech/design-system';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { CreateModelStep3FormData } from 'pages/Models/AnalyticsModelTab/CreateModelStep3';
import { useMemo } from 'react';
import { RegisterOptions, useFieldArray, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { v4 as uuid } from 'uuid';

const nameMaxLength = 50;
const unitMaxLength = 50;

const unitRules: RegisterOptions = {
    maxLength: {
        value: unitMaxLength,
        message: `Indicator unit cannot exceed ${unitMaxLength} characters.`,
    },
};

const Indicator = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    column-gap: 2rem;
    row-gap: 1rem;
    align-items: center;
`;

export type AnalyticsIndicatorsType = 'feature' | 'smart';

export type AnalyticsIndicatorsInputProps = Readonly<{
    form: UseFormReturn<CreateModelStep3FormData>;
    type: AnalyticsIndicatorsType;
}>;

export function AnalyticsIndicatorsInput({
    form: {
        control,
        formState: { errors, isValidating },
        getValues,
        register,
    },
    type,
}: AnalyticsIndicatorsInputProps) {
    const collectionName = type === 'feature' ? 'featureIndicators' : 'smartIndicators';

    const {
        fields: indicators,
        append: appendIndicator,
        remove: removeIndicator,
    } = useFieldArray({
        control,
        name: collectionName,
        shouldUnregister: false,
    });

    const nameRules = useMemo<RegisterOptions>(
        () => ({
            required: 'Indicator name is required.',
            maxLength: {
                value: nameMaxLength,
                message: `Indicator name cannot exceed ${nameMaxLength} characters.`,
            },
            validate: (value: string) => {
                const { featureIndicators, smartIndicators } = getValues();
                const allIndicators = [...featureIndicators, ...smartIndicators];
                const notUnique =
                    allIndicators.filter(({ name }) => name === value).length > 1;

                return notUnique ? 'Indicator name must be unique' : undefined;
            },
        }),
        [getValues]
    );

    const addNewIndicator = () => {
        appendIndicator({
            key: uuid(),
            name: '',
            unit: '',
        });
    };

    return (
        <>
            {indicators.map((indicator, index) => (
                <Indicator key={indicator.key}>
                    <div>
                        <TextInput
                            required
                            label="Indicator Name"
                            placeholder="Enter parameter name"
                            error={errors[collectionName]?.[index]?.name?.message}
                            {...register(
                                `${collectionName}.${index}.name` as const,
                                nameRules
                            )}
                        />
                    </div>
                    <div>
                        <TextInput
                            label="Unit"
                            placeholder="Enter indicator unit"
                            error={errors[collectionName]?.[index]?.unit?.message}
                            {...register(
                                `${collectionName}.${index}.unit` as const,
                                unitRules
                            )}
                        />
                    </div>
                    <div>
                        <HoverIconButton
                            icon={faTrash}
                            hoverIcon={faTrashSolid}
                            color={tokens.text.secondary}
                            hoverColor={tokens.error.default}
                            size="14"
                            onClick={() => removeIndicator(index)}
                            title="Remove indicator"
                        />
                    </div>
                </Indicator>
            ))}
            <div className="text-right">
                <Button type="button" onClick={addNewIndicator} disabled={isValidating}>
                    Add {type === 'feature' ? 'Feature' : 'Smart'} Indicator
                </Button>
            </div>
        </>
    );
}
